import { useEffect, useRef } from "react";
import { animate, motion, useInView, useMotionValue, useTransform } from "framer-motion";
import { Eyebrow, Reveal, SectionShell } from "./Shared";

function Counter({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });
  const mv = useMotionValue(0);
  const val = useTransform(mv, (v) => `${Math.round(v)}${suffix}`);

  useEffect(() => {
    if (inView) {
      const controls = animate(mv, to, { duration: 1.8, ease: [0.22, 1, 0.36, 1] });
      return () => controls.stop();
    }
  }, [inView, mv, to]);

  return (
    <span ref={ref} data-testid="lineage-counter">
      <motion.span>{val}</motion.span>
    </span>
  );
}

const MILESTONES = [
  { display: <Counter to={20} suffix="+" />, label: "Years in sports representation" },
  {
    display: <span className="text-3xl sm:text-4xl">NFL · MLB</span>,
    sub: "Combat sports · Action sports",
    label: "Where our team comes from",
  },
  { display: <span className="text-4xl sm:text-5xl">2023</span>, label: "Traditional division sold" },
  {
    display: <span className="text-4xl sm:text-5xl text-volt">2026</span>,
    label: "SMG Sports Management launch",
  },
];

export default function Lineage() {
  return (
    <SectionShell id="lineage">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="flex h-full flex-col lg:col-span-5">
          <Reveal>
            <Eyebrow className="mb-5">— 01 / The Lineage</Eyebrow>
            <h2 className="font-display text-4xl uppercase leading-[1.05] sm:text-5xl lg:text-6xl">
              Two decades in sports.{" "}
              <span className="text-volt">One new chapter.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <blockquote className="mt-10 border-l-2 border-volt pl-6 text-lg leading-relaxed text-slate-200 sm:text-xl">
              "This is more than athlete representation. This is the next
              generation of pickleball management."
            </blockquote>
          </Reveal>
          <Reveal delay={0.25} className="mt-10 min-h-[300px] flex-1">
            <div
              className="relative h-full min-h-[300px]"
              data-testid="lineage-foundation-visual"
            >
              <div className="img-blend absolute inset-0">
                <img
                  src="https://images.unsplash.com/photo-1737476997205-b3336182f215?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Pickleball action on court"
                  className="h-full w-full object-cover opacity-90 grayscale"
                />
              </div>
              <div className="absolute bottom-2 left-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-volt">
                  The Foundation
                </p>
                <p className="mt-1 font-display text-sm uppercase text-white">
                  Two decades behind every SMG signing
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="lineage-milestones">
              {MILESTONES.map((m, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-white/10 bg-surface p-5 transition-colors hover:border-volt/40"
                >
                  <div className="font-display text-4xl text-white sm:text-5xl">
                    {m.display}
                  </div>
                  {m.sub && (
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-volt">
                      {m.sub}
                    </p>
                  )}
                  <p className="mt-2 text-xs leading-relaxed text-slate-400">{m.label}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-10 space-y-6 text-base leading-relaxed text-slate-300">
              <p>
                For more than twenty years, our team has represented and worked
                alongside some of the most recognizable names in professional
                sports — NFL Sundays, October baseball, championship nights in
                combat and action sports, and the personalities shaping today's
                culture.
              </p>
              <p>
                In 2023, founder Brian Stretch sold the firm's traditional
                representation division. The exit wasn't the end of the story —
                it was a clean sheet. With the launch of SMG Media Group, Brian
                built the engine: brand, creative, media, AI and partnership
                infrastructure under one roof.
              </p>
              <p>
                Pickleball gave us the mission. SMG Sports Management Group
                International is a pickleball-first agency built for the
                fastest-growing sport in the world — pairing two decades of
                experience and foresight with a new generation of ambitious
                agents.
              </p>
              <p className="font-semibold text-white">
                Our mission is clear: represent the athletes shaping pickleball's
                future, build their brands beyond the court, and connect them
                with opportunities that match their talent, personality and
                long-term vision.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </SectionShell>
  );
}
